import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, Check, MessageCircle } from "lucide-react";
import { useToast } from "../hooks/use-toast";

export const CONTACT = {
  phone: "+91 98453 42025",
  phoneDial: "+919845342025",
  whatsapp: "+91 98453 42025",
  whatsappNum: "919845342025",
  email: "admin@tatvawellnesscenter.in",
  mapUrl:
    "https://www.google.com/maps/search/?api=1&query=3HXF%2BV2+Bengaluru+Karnataka",
  city: "Yelahanka New Town, Bengaluru",
};

const BookCTA = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: "Please provide your name and phone." });
      return;
    }

    // Save locally as a lightweight record
    const list = JSON.parse(localStorage.getItem("tatva_bookings") || "[]");
    list.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("tatva_bookings", JSON.stringify(list));

    // Compose a friendly WhatsApp message with the booking details
    const lines = [
      "Namaste Tatva Wellness Center!",
      "I would like to book a consultation.",
      "",
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
    ];
    if (form.email) lines.push(`Email: ${form.email}`);
    if (form.message) {
      lines.push("");
      lines.push(`Message: ${form.message}`);
    }
    const text = encodeURIComponent(lines.join("\n"));
    const waUrl = `https://wa.me/${CONTACT.whatsappNum}?text=${text}`;

    // Open WhatsApp in a new tab
    window.open(waUrl, "_blank", "noopener,noreferrer");

    setSubmitted(true);
    toast({
      title: "Opening WhatsApp…",
      description: "Please tap Send in WhatsApp to complete your request.",
    });
    setTimeout(() => setSubmitted(false), 5000);
    setForm({ name: "", phone: "", email: "", message: "" });
  };

  return (
    <section id="book" className="relative bg-white py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-72 h-72 rounded-full bg-[#eaf1e6] blur-3xl opacity-70" />
        <div className="absolute bottom-10 right-10 w-72 h-72 rounded-full bg-[#f4ecd8] blur-3xl opacity-70" />
      </div>

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#c4a257]" />
              <span className="text-[#c4a257] text-xs tracking-[0.3em] uppercase font-medium">
                Begin Your Journey
              </span>
            </div>
            <h2 className="font-serif text-[38px] md:text-[54px] leading-[1.05] text-[#1b5e3a] mb-6">
              Reconnect. Rebalance.
              <br />
              <span className="italic text-[#2c7a4a]">Rediscover You.</span>
            </h2>
            <p className="text-[#3a4a3d] leading-[1.75] mb-10">
              Take the first step toward lasting wellness. Our team is ready to
              listen, understand, and guide you back to your natural state of
              health.
            </p>

            <div className="space-y-4">
              <a
                href={`tel:${CONTACT.phoneDial}`}
                className="group flex items-center gap-4 p-4 rounded-xl border border-[#efe7d5] hover:border-[#c4a257] bg-[#fbf9f4] hover:bg-white transition-all"
              >
                <span className="w-11 h-11 rounded-full bg-[#eaf1e6] flex items-center justify-center group-hover:bg-[#1b5e3a] group-hover:text-white text-[#1b5e3a] transition-colors">
                  <Phone className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#6b7568]">
                    Call Us Now
                  </p>
                  <p className="font-serif text-[#1b5e3a] text-lg">{CONTACT.phone}</p>
                </div>
              </a>
              <a
                href={`https://wa.me/${CONTACT.whatsappNum}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 p-4 rounded-xl border border-[#efe7d5] hover:border-[#c4a257] bg-[#fbf9f4] hover:bg-white transition-all"
              >
                <span className="w-11 h-11 rounded-full bg-[#eaf1e6] flex items-center justify-center group-hover:bg-[#25D366] group-hover:text-white text-[#1b5e3a] transition-colors">
                  <MessageCircle className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#6b7568]">
                    WhatsApp Us
                  </p>
                  <p className="font-serif text-[#1b5e3a] text-lg">{CONTACT.whatsapp}</p>
                </div>
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                className="group flex items-center gap-4 p-4 rounded-xl border border-[#efe7d5] hover:border-[#c4a257] bg-[#fbf9f4] hover:bg-white transition-all"
              >
                <span className="w-11 h-11 rounded-full bg-[#eaf1e6] flex items-center justify-center group-hover:bg-[#1b5e3a] group-hover:text-white text-[#1b5e3a] transition-colors">
                  <Mail className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#6b7568]">
                    Email Us
                  </p>
                  <p className="font-serif text-[#1b5e3a] text-lg break-all">{CONTACT.email}</p>
                </div>
              </a>
              <a
                href={CONTACT.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 p-4 rounded-xl border border-[#efe7d5] hover:border-[#c4a257] bg-[#fbf9f4] hover:bg-white transition-all"
              >
                <span className="w-11 h-11 rounded-full bg-[#eaf1e6] flex items-center justify-center group-hover:bg-[#1b5e3a] group-hover:text-white text-[#1b5e3a] transition-colors">
                  <MapPin className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#6b7568]">
                    Visit Us
                  </p>
                  <p className="font-serif text-[#1b5e3a] text-lg">{CONTACT.city}</p>
                </div>
              </a>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            className="relative bg-white border border-[#efe7d5] rounded-2xl p-8 md:p-10 shadow-[0_24px_60px_rgba(27,94,58,0.1)]"
          >
            <h3 className="font-serif text-2xl text-[#1b5e3a] mb-2">
              Book a Consultation
            </h3>
            <p className="text-sm text-[#6b7568] mb-6">
              Share a few details and we&apos;ll reach out to plan your visit.
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-[#6b7568] font-medium">
                  Full Name
                </label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full bg-[#fbf9f4] border border-[#efe7d5] rounded-lg px-4 py-3 text-[#2c342d] focus:outline-none focus:border-[#1b5e3a] focus:ring-2 focus:ring-[#1b5e3a]/15 transition"
                  placeholder="Your name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-[#6b7568] font-medium">
                    Phone
                  </label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1 w-full bg-[#fbf9f4] border border-[#efe7d5] rounded-lg px-4 py-3 text-[#2c342d] focus:outline-none focus:border-[#1b5e3a] focus:ring-2 focus:ring-[#1b5e3a]/15 transition"
                    placeholder="+91 …"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-[#6b7568] font-medium">
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1 w-full bg-[#fbf9f4] border border-[#efe7d5] rounded-lg px-4 py-3 text-[#2c342d] focus:outline-none focus:border-[#1b5e3a] focus:ring-2 focus:ring-[#1b5e3a]/15 transition"
                    placeholder="you@example.com"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-[#6b7568] font-medium">
                  How can we help?
                </label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4}
                  className="mt-1 w-full bg-[#fbf9f4] border border-[#efe7d5] rounded-lg px-4 py-3 text-[#2c342d] focus:outline-none focus:border-[#1b5e3a] focus:ring-2 focus:ring-[#1b5e3a]/15 transition resize-none"
                  placeholder="Tell us briefly about your goals or concerns…"
                />
              </div>
              <button
                type="submit"
                className="group w-full inline-flex items-center justify-center gap-3 bg-[#1b5e3a] hover:bg-[#164d30] text-white text-sm font-semibold tracking-[0.14em] uppercase px-8 py-4 rounded-md shadow-[0_10px_30px_rgba(27,94,58,0.3)] transition-all duration-300 hover:-translate-y-0.5"
              >
                {submitted ? (
                  <>
                    <Check className="w-4 h-4" /> Sent to WhatsApp
                  </>
                ) : (
                  <>
                    Send via WhatsApp
                    <Send className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
              <p className="text-[11px] text-[#6b7568] text-center mt-1">
                We&apos;ll reply on WhatsApp within one working day.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default BookCTA;
